import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Container,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Science as ScienceIcon,
  Bolt as BoltIcon,
  ContentCopy as ContentCopyIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import {
  PurpleText,
  SectionContainer,
  SectionSubtitle,
  SectionTitle,
} from '../common/components';

interface Metric {
  label: string;
  value: number;
  suffix?: string;
  description: string;
  icon: React.ReactNode;
}

interface CapabilityArea {
  title: string;
  description: string;
  focus: string[];
  icon: React.ReactNode;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const AnimatedMetric = ({ value }: { value: number }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setDisplayValue(value);
      return;
    }

    let start: number | null = null;
    let animationFrameId = 0;

    const step = (timestamp: number) => {
      if (start === null) {
        start = timestamp;
      }
      const progress = Math.min((timestamp - start) / 1200, 1);
      const eased = easeOutCubic(progress);
      setDisplayValue(Math.round(eased * value));

      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      }
    };

    animationFrameId = window.requestAnimationFrame(step);

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [value]);

  return <>{displayValue}</>;
};

export const CodingHighlights = (): React.ReactNode => {
  const [copied, setCopied] = useState(false);

  const metrics: Metric[] = useMemo(
    () => [
      {
        label: 'Production features shipped',
        value: 28,
        suffix: '+',
        description:
          'end-to-end user journeys delivered with CI/CD automation.',
        icon: <AutoAwesomeIcon fontSize='large' color='primary' />,
      },
      {
        label: 'Automated tests authored',
        value: 160,
        suffix: '+',
        description:
          'unit, integration, and contract tests protecting critical flows.',
        icon: <ScienceIcon fontSize='large' color='secondary' />,
      },
      {
        label: 'Microservices deployed',
        value: 9,
        description:
          'cloud-native services instrumented with logging and observability.',
        icon: <BoltIcon fontSize='large' sx={{ color: '#f9a825' }} />,
      },
    ],
    []
  );

  const capabilityAreas: CapabilityArea[] = useMemo(
    () => [
      {
        title: 'Full-Stack Product Engineering',
        description:
          'Design resilient product experiences that pair thoughtful UX with scalable backend APIs and data models.',
        focus: [
          'React + TypeScript',
          'Ruby on Rails',
          'GraphQL / REST',
          'Performance profiling',
        ],
        icon: <AutoAwesomeIcon color='primary' />,
      },
      {
        title: 'Platform & DevOps',
        description:
          'Automate delivery pipelines, implement infrastructure-as-code, and champion observability for confident releases.',
        focus: [
          'Docker & AWS',
          'CI/CD pipelines',
          'Telemetry & alerting',
          'Developer experience',
        ],
        icon: <BoltIcon sx={{ color: '#f9a825' }} />,
      },
      {
        title: 'Quality Engineering Leadership',
        description:
          'Build robust testing strategies, mentor engineers on best practices, and keep feedback loops fast and informative.',
        focus: [
          'Testing pyramids',
          'Property-based testing',
          'Pair programming',
          'Code review facilitation',
        ],
        icon: <ScienceIcon color='secondary' />,
      },
    ],
    []
  );

  const codeSnippet = `export const useFeatureFlag = (flag: string) => {\n  const { data } = useQuery(['feature-flag', flag], fetchFeatureFlag, {\n    staleTime: 5 * 60 * 1000,\n  });\n\n  return useMemo(() => ({\n    isEnabled: Boolean(data?.enabled),\n    rolloutPercent: data?.rollout ?? 0,\n  }), [data]);\n};`;

  const handleCopySnippet = () => {
    if (typeof window === 'undefined' || !window.navigator?.clipboard) {
      return;
    }

    void window.navigator.clipboard
      .writeText(codeSnippet)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => setCopied(false));
  };

  return (
    <SectionContainer>
      <Container maxWidth='lg'>
        <SectionTitle variant='h2'>
          Engineering <PurpleText>Highlights</PurpleText>
        </SectionTitle>

        <SectionSubtitle variant='body1'>
          A quick snapshot of the engineering value I love to deliver — from
          automation-first development to thoughtful product craftsmanship.
        </SectionSubtitle>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          sx={{ mb: 8 }}
        >
          {metrics.map(metric => (
            <Paper
              key={metric.label}
              elevation={0}
              sx={{
                flex: 1,
                p: 4,
                background:
                  'linear-gradient(140deg, rgba(10, 19, 36, 0.9), rgba(7, 29, 53, 0.9))',
                border: '1px solid rgba(34, 211, 238, 0.2)',
                borderRadius: 4,
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {metric.icon}
                <Typography variant='h5' sx={{ fontWeight: 700 }}>
                  <AnimatedMetric value={metric.value} />
                  {metric.suffix}
                </Typography>
              </Box>

              <Typography
                variant='subtitle1'
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {metric.label}
              </Typography>

              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {metric.description}
              </Typography>
            </Paper>
          ))}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 4,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background:
                'linear-gradient(140deg, rgba(10, 19, 36, 0.9), rgba(7, 29, 53, 0.9))',
              border: '1px solid rgba(34, 211, 238, 0.2)',
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Typography variant='h5' sx={{ fontWeight: 700 }}>
              Capabilities I bring to teams
            </Typography>

            {capabilityAreas.map(area => (
              <Paper
                key={area.title}
                elevation={0}
                sx={{
                  p: 3,
                  background: 'rgba(13, 26, 46, 0.75)',
                  borderRadius: 3,
                  border: '1px solid rgba(34, 211, 238, 0.12)',
                }}
              >
                <Stack direction='row' spacing={2} alignItems='flex-start'>
                  {area.icon}
                  <Box>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      {area.title}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', mb: 2 }}
                    >
                      {area.description}
                    </Typography>
                    <Stack
                      direction='row'
                      spacing={1}
                      flexWrap='wrap'
                      useFlexGap
                    >
                      {area.focus.map(item => (
                        <Chip
                          key={item}
                          label={item}
                          variant='outlined'
                          sx={{
                            borderColor: 'rgba(199, 112, 240, 0.4)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            mb: 1,
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              background:
                'linear-gradient(140deg, rgba(10, 19, 36, 0.9), rgba(7, 29, 53, 0.9))',
              border: '1px solid rgba(34, 211, 238, 0.2)',
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Stack direction='row' spacing={2} alignItems='center'>
              <TaskAltIcon color='primary' />
              <Typography variant='h5' sx={{ fontWeight: 700 }}>
                Craftsmanship in practice
              </Typography>
            </Stack>

            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              From feature flags to automated pipelines, I love solving product
              problems with elegant, maintainable code. Here&apos;s a quick look
              at a reusable hook I use to keep feature experiments reliable.
            </Typography>

            <Paper
              elevation={0}
              sx={{
                background: 'rgba(13, 17, 23, 0.95)',
                border: '1px solid rgba(199, 112, 240, 0.25)',
                borderRadius: 3,
                fontFamily: '"Fira Code", "Source Code Pro", monospace',
                fontSize: 14,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid rgba(199, 112, 240, 0.25)',
                  px: 2,
                  py: 1,
                }}
              >
                <Typography
                  variant='caption'
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  useFeatureFlag.ts
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy snippet'}>
                  <span>
                    <IconButton
                      size='small'
                      onClick={handleCopySnippet}
                      disabled={copied}
                      sx={{
                        color: copied
                          ? 'success.main'
                          : 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      {copied ? (
                        <TaskAltIcon fontSize='small' />
                      ) : (
                        <ContentCopyIcon fontSize='small' />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Box component='pre' sx={{ m: 0, p: 2, whiteSpace: 'pre-wrap' }}>
                {codeSnippet}
              </Box>
            </Paper>
          </Paper>
        </Box>
      </Container>
    </SectionContainer>
  );
};
